import { DEFAULT_ACTIVE_STATUSES, PAYMENT_FREQUENCIES } from "./paymentSupport";
import { CalendarMonth as CalendarIcon } from "@mui/icons-material";
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { currencyFormat } from "common/numbers";
import IconButton from "components/common/IconButton";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import moment from "moment";
import { useState } from "react";
import { useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as programPricingStore from "store/programPricing";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import { shortLayerName } from "utils";

const adjustForWeekend = (date) => {
  const day = date.day();
  if (day === 6) return date.clone().add(2, "days");
  if (day === 0) return date.clone().add(1, "day");
  return date;
};

const buildSchedule = (inception, premium, frequency) => {
  const config = PAYMENT_FREQUENCIES.find((f) => f.key === frequency);
  if (!config || !inception || !premium) return [];
  const base = moment.parseZone(inception);
  const installment = premium / config.count;
  return Array.from({ length: config.count }, (_, i) => ({
    date: adjustForWeekend(base.clone().add(i * config.months, "months")),
    amount: installment,
  }));
};

const layerLabel = (layer) =>
  layer?.limit != null
    ? shortLayerName(layer)
    : layer?.reference ?? layer?.id ?? "Layer";

const LayerHeader = ({ layer }) => (
  <Stack
    direction={"row"}
    alignItems={"center"}
    justifyContent={"flex-end"}
    gap={0.5}
  >
    <StatusBadgeV2 status={layer.status} variant={"icon"} />
    <Typography fontSize={"0.8rem"} fontWeight={600} noWrap>
      {layerLabel(layer)}
    </Typography>
  </Stack>
);

const ScheduleTable = ({ layers, inception, frequency }) => {
  const schedules = layers.map((layer) => {
    const premium =
      layer?.shareOfPremium ??
      layer?.grossPremium ??
      layer?.brokerTargetGrossPremium;
    return {
      layer,
      schedule: buildSchedule(inception, premium, frequency),
      premium,
    };
  });

  const rowCount = schedules[0]?.schedule?.length ?? 0;

  return (
    <Table size={"small"} sx={{ width: "auto" }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 30, whiteSpace: "nowrap" }}>{"#"}</TableCell>
          <TableCell sx={{ width: 120, whiteSpace: "nowrap" }}>
            {"Due Date"}
          </TableCell>
          {schedules.map(({ layer }) => (
            <TableCell key={layer.id} align={"right"}>
              <LayerHeader layer={layer} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rowCount }, (_, i) => (
          <TableRow key={i}>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{i + 1}</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {schedules[0]?.schedule[i]?.date.format("MMM D, YYYY")}
            </TableCell>
            {schedules.map(({ layer, schedule }) => (
              <TableCell key={layer.id} align={"right"}>
                {schedule[i] ? currencyFormat(schedule[i].amount) : "—"}
              </TableCell>
            ))}
          </TableRow>
        ))}
        <TableRow>
          <TableCell colSpan={2}>
            <Typography variant={"body2"} fontWeight={"bold"}>
              {"Total"}
            </Typography>
          </TableCell>
          {schedules.map(({ layer, premium }) => (
            <TableCell key={layer.id} align={"right"}>
              <Typography variant={"body2"} fontWeight={"bold"}>
                {premium ? currencyFormat(premium) : "—"}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );
};

export const PaymentScheduleButton = () => {
  const [open, setOpen] = useState(false);
  const [activeStatuses, setActiveStatuses] = useState(DEFAULT_ACTIVE_STATUSES);

  const programPricing = useSelector(programPricingStore.selectInput);
  const inception = useSelector(programSelectors.getInception);
  const layers = useSelector(pricingSelectors.selectResolvedLayers);
  const { data: statusLabels } = staticDataService.useConfigQuery(
    "layer_status_labels_v2"
  );
  const allStatuses = (statusLabels?.labels ?? []).filter(
    (s) => s.statusKey && s.statusKey !== "DIVIDER"
  );

  const frequency = programPricing?.paymentFrequency;
  const frequencyLabel = PAYMENT_FREQUENCIES.find((f) => f.key === frequency)
    ?.name;

  const toggleStatus = (statusKey) =>
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      next.has(statusKey) ? next.delete(statusKey) : next.add(statusKey);
      return next;
    });

  const filteredLayers = layers?.filter(
    (l) => !l.status || activeStatuses.has(l.status)
  );

  if (!frequency) return null;

  return (
    <>
      <IconButton
        icon={CalendarIcon}
        variant={"bright"}
        tooltip={"View Payment Schedule"}
        onClick={() => setOpen(true)}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={"lg"}
        fullWidth
        PaperProps={{ sx: { m: 2 } }}
      >
        <DialogTitle>
          <Typography
            variant={"h6"}
          >{`Payment Schedule — ${frequencyLabel}`}</Typography>
          {inception && (
            <Typography variant={"body2"} color={"text.secondary"}>
              {`From: ${moment.parseZone(inception).format("MMM D, YYYY")}`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1 }}>
          <Stack spacing={1.5}>
            <Stack direction={"row"} flexWrap={"wrap"} gap={0.5}>
              {allStatuses.map((statusMeta) => {
                const active = activeStatuses.has(statusMeta.statusKey);
                return (
                  <Chip
                    key={statusMeta.statusKey}
                    label={<StatusBadgeV2 meta={statusMeta} />}
                    size={"small"}
                    onClick={() => toggleStatus(statusMeta.statusKey)}
                    variant={active ? "filled" : "outlined"}
                    color={active ? "primary" : "default"}
                    sx={{
                      "& .MuiChip-label": {
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  />
                );
              })}
            </Stack>
            {filteredLayers?.length > 0 ? (
              <ScheduleTable
                layers={filteredLayers}
                inception={inception}
                frequency={frequency}
              />
            ) : (
              <Typography color={"text.secondary"}>
                {"No layers match the selected statuses."}
              </Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentScheduleButton;
